import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { SortOption } from '../../interfaces/sort-option.interface';
import { filter, map, shareReplay } from 'rxjs/operators';
import { SellSortOptions } from '../sell-sort-options/sell-sort-options';
import { Store } from '@ngxs/store';
import {
  LaunchDarklyService,
  InventoryManagementState,
  InventoryManagementAuctionItemStatus,
  InventoryManagementSearchRequest
} from 'remarketing-angular-library';
import { selectHasMembers } from '../lead-seller-group/reducer';
import { LeadSellerGroupService } from '../lead-seller-group/lead-seller-group.service';
import { defaultSortForStatus, sellSortFromParams } from '../sell-sort-helper/sell-sort.helper';

@Injectable({
  providedIn: 'root'
})
export class SellSortServiceV2 {
  readonly inventorySearchRequest$ = this.store.select(InventoryManagementState.searchRequest)

  hasLeadSellerMembers$: Observable<boolean> = this.leadSellerGroupService.select(selectHasMembers);

  constructor(
    private readonly store: Store,
    readonly leadSellerGroupService: LeadSellerGroupService,
    readonly launchDarklyService: LaunchDarklyService,
    private readonly sellSortOptions: SellSortOptions
  ) { }

  readonly sortOptions$: Observable<SortOption[]> = combineLatest([this.hasLeadSellerMembers$, this.inventorySearchRequest$]).pipe(
    filter(([, request]) => !!request),
    map(([hasLeadMemberSellers, request]) => {
      let sortOptions;
      switch (request.searchCriteria.filterByStatus) {
        case InventoryManagementAuctionItemStatus.SOLD:
          sortOptions = this.sellSortOptions.sold();
          break;
        case InventoryManagementAuctionItemStatus.HOLD:
          sortOptions = this.sellSortOptions.hold();
          break;
        case InventoryManagementAuctionItemStatus.REMOVED:
          sortOptions = this.sellSortOptions.removed();
          break;
        case null:
          sortOptions = this.sellSortOptions.all();
          break;
        case InventoryManagementAuctionItemStatus.ACTIVE:
        default:
          sortOptions = this.sellSortOptions.active();
          break;
      }
      sortOptions = this.handleSortPermissions(sortOptions, hasLeadMemberSellers);
      return this.mapToSelected(sortOptions, request);
    }),
    shareReplay(1)
  );

  private mapToSelected(options: SortOption[], request: InventoryManagementSearchRequest): SortOption[] {
    let hasSelected = false;

    const { sortBy = '', sortOrder = '' } = request?.searchCriteria || {};
    let sortValue = sellSortFromParams(sortBy, sortOrder);

    if (typeof sortValue === 'undefined') {
      sortValue = defaultSortForStatus(request.searchCriteria.filterByStatus as InventoryManagementAuctionItemStatus);
    }

    const mappedOptions = options.map(sortOption => {
      return {
        header: sortOption.header,
        options: sortOption.options.map(option => {
          const selected = option.value === sortValue;
          if (selected) {
            hasSelected = true;
          }
          return { ...option, selected };
        })
      };
    });

    if (!hasSelected) {
      // This will always be called with an array at least this long
      mappedOptions[0].options[0].selected = true;
    }

    return mappedOptions;
  }

  private handleSortPermissions(sortOptions: SortOption[], hasLeadMemberSellers: boolean) {
    let sortedOptions = [...sortOptions];

    if (!hasLeadMemberSellers) { // if you do not have the permission 
      // filter to only items that do not require permission
      sortedOptions = sortedOptions.filter(x => !x.requiresLeadSeller);
    }
    if (!this.isSellUpdateInventoryFeatureEnabled) {
      sortedOptions = sortedOptions.filter(x => !x.requiresSellUpdateInventory);
    }
    if (!this.isTenantBadgeEnabled) {
      sortedOptions = sortedOptions.filter(x => !x.requiresSellTenantBadge);
    }

    return sortedOptions;
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get isTenantBadgeEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-tenantBadgeEnabled', false);
  }
}
